import { tagged } from "@mirage/service-logging";
import {
  ONE_HOUR_IN_MILLIS,
  ONE_MINUTE_IN_MILLIS,
} from "@mirage/shared/util/constants";
import { utcToZonedTime } from "date-fns-tz";

import type { dash } from "@dropbox/api-v2-client";
import type { APIv2Callable } from "@mirage/service-dbx-api/service";

const logger = tagged("service-calendar-events");

/**
 * NOTE: To support Google Calendar's API more accurately we should consider:
 * - including start_date and end_date in the SearchResult API
 * - including response_status in the SearchResult API for the current user, or for attendees
 */
export const getUpcomingCalendarEvents = (
  callApiV2: APIv2Callable,
): Promise<dash.SearchResult[] | undefined> => {
  // Get meetings that started 10 minutes ago to 12 hours from now
  const startDateTime = Date.now() - ONE_MINUTE_IN_MILLIS * 10;
  const endDateTime = Date.now() + ONE_HOUR_IN_MILLIS * 12;
  return callApiV2("dcsGetCalendarEvents", {
    start_datetime: new Date(startDateTime).toISOString(),
    end_datetime: new Date(endDateTime).toISOString(),
    include_ongoing: true,
    include_all_day: true,
    user_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  })
    .then((r) => {
      return r.events
        ?.map((event) => {
          const [startTime, endTime] = event.is_all_day
            ? fixAllDayTimes(event.start_time || 0, event.end_time || 0)
            : [event.start_time || 0, event.end_time || 0];

          return {
            ...event,
            start_time: startTime,
            end_time: endTime,
            is_all_day: supplementAllDayFlagWithLongEvents(
              event.is_all_day,
              startTime,
              endTime,
            ),
          };
        })
        .sort((a, b) => {
          // Shorter events first
          if (a.start_time === b.start_time) {
            return a.end_time - b.end_time;
          }
          return a.start_time - b.start_time;
        });
    })
    .catch((e) => {
      logger.error("Failed to fetch calendar events", e);
      return undefined;
    });
};

// TODO: Temporary fix until https://jira.dropboxer.net/browse/OTCKA-506 is
// resolved and the API provides all-day events with timestamps in the same
// offset as what is requested by user_time_zone. Currently the search is
// done with the correct offset, but the returned timestamps are still in
// the incorrect "shifted" UTC offset.
export function fixAllDayTimes(
  startTime: number,
  endTime: number,
): [number, number] {
  return [
    utcToZonedTime(startTime, "UTC").getTime(),
    utcToZonedTime(endTime, "UTC").getTime(),
  ];
}

/**
 * We are mirroring the rules set by Calendar Agent where if an event is marked  by the
 * is_all_day flag OR the event lasts longer than 23hr but is not officially marked by the is_all_day flag
 * we treat the event as an is_all_day event.
 *
 * This ensures we actually capture the intent of is_all_day accurately.
 */
export function supplementAllDayFlagWithLongEvents(
  isAllDay: boolean | undefined,
  startTime: number,
  endTime: number,
): boolean {
  if (isAllDay) return true;

  // If the SearchResult API exposed start_date (not just start_time) and end_date (not just end_time)
  // we could use that to determine if the event is_all_day.
  // This is advised based on idiomatic Google Calendar creation rules, and is included in
  // the Calendar Agent implementation for defining is_all_day.
  // if (event.start_date || event.end_date) {
  //   return true;
  // }

  // Because the API only exposes start_time and end_time, we can only rely on time
  // Here we check if the event lasts longer than 23 hours.
  const eventDuration = endTime - startTime;
  const twentyThreeHoursInMillis = ONE_HOUR_IN_MILLIS * 23;
  return eventDuration >= twentyThreeHoursInMillis;
}
